import { createApiClient, createQuery } from "@/api/utils";
import { LineageMetric, Metric } from "./Finance.model";
import { AccountLabel, PeriodKind } from "../Labeling/Account.model";
import parseLineageMetric from "./parseLineageMetric";

export const getMetrics = createQuery<
    Metric[],
    { labels: AccountLabel[]; periodKind: PeriodKind }
>(async ({ api, router, params }) => {
    const response = await api.get(
        `/finance/companies/${router.params.companyId}/metrics?period_kind=${params.periodKind}&value_kind=lineage${params.labels.reduce(
            (acc, curr) => {
                return `${acc}&label=${curr}`;
            },
            "",
        )}`,
    );

    return response.data;
});

export const getMetricsV2 = createQuery<
    Metric[],
    { metricQuery: string | string[] }
>(async ({ router, session, params }) => {
    const metricQuery =
        params.metricQuery ?? (router.search["metric-query"] as string);

    if (Array.isArray(metricQuery) ? metricQuery.length === 0 : !metricQuery) {
        return [];
    }

    const queries = Array.isArray(metricQuery) ? metricQuery : [metricQuery];
    const queryString = queries
        .map((q) => `m=${encodeURIComponent(q)}`)
        .join("&");

    const response = await createApiClient({
        token: session.token.data || undefined,
    }).get(
        `${process.env.FINANCE_API_URL}/api/beta/companies/${router.params.companyId}/metric-query?value_kind=lineage&${queryString}`,
    );

    return response.data;
});

export const getLineageMetrics = createQuery<
    LineageMetric[],
    { labels: AccountLabel[] }
>(async ({ api, params, router }) => {
    const response = await api.get(
        `/finance/companies/${router.params.companyId}/metrics?period_kind=monthly&value_kind=lineage${params.labels.reduce(
            (acc, curr) => {
                return `${acc}&label=${curr}`;
            },
            "",
        )}`,
    );

    return (Array.isArray(response.data) ? response.data : []).map(
        parseLineageMetric,
    );
});

export const getAccount = createQuery<
    { id: string; name: string; label: AccountLabel },
    { accountId: string }
>(async ({ api, params }) => {
    const response = await api.get(`/finance/accounts/${params.accountId}`);

    return response.data;
});

export const getAccounts = createQuery<
    Record<string, { id: string; name: string; label: string }>
>(async ({ api, router }) => {
    const response = await api.get(
        `/finance/accounts?limit=1000&company_id=${router.params.companyId}`,
    );

    return (
        response.data?.reduce(
            (acc, curr) => {
                acc[curr.id] = curr;
                return acc;
            },
            {} as Record<string, { id: string; name: string; label: string }>,
        ) || {}
    );
});
